/**
 * LO ÚLTIMO QUE SE VIO DE CADA PANTALLA, GUARDADO EN EL DISPOSITIVO.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  EL PROBLEMA, CON LAS PALABRAS DEL DUEÑO
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 *     «lo estuve usando sin internet y prácticamente nada carga, y eso quiero
 *      que lo cambiemos: solo funciona para hacer ventas»
 *
 * Y era exacto. El punto de venta guardaba su catálogo y por eso vendía sin
 * señal; el resto de Softfi no guardaba nada. Sin internet, el inventario, las
 * cuentas por cobrar, las monedas, el equipo, los reportes, la caja — todo
 * enseñaba un error o una pantalla en blanco.
 *
 * Y son datos que la persona YA VIO hace diez minutos. Que desaparezcan porque
 * se cayó el wifi no protege de nada: solo obliga a salir a buscar señal para
 * mirar algo que el aparato tenía delante.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  CÓMO FUNCIONA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * React Query ya guarda en memoria la respuesta de cada pantalla. Lo único que
 * faltaba era que sobreviviera a cerrar la aplicación. Así que:
 *
 *   1. Al abrir, se lee lo guardado y se mete en la caché ANTES del primer
 *      dibujo. Cada pantalla nace con lo último que se vio de ella.
 *   2. Cada vez que llega algo nuevo, se vuelve a guardar (con calma: ver
 *      `ESPERA`).
 *   3. Lo restaurado nace VIEJO a propósito. Con internet, la pantalla lo
 *      enseña al instante y pide lo de hoy por detrás; sin internet, se queda
 *      con eso y ya.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ LO QUE ESTO NO ES
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * **No es una base de datos que se pueda modificar sin señal.** Es una foto de
 * lo último que se vio, para MIRAR. Las dos cosas que de verdad se pueden hacer
 * sin internet —vender y registrar un cliente— tienen su propia cola y su
 * propio envío (`stores/offline.ts`), porque ahí hay dinero de por medio y una
 * foto no basta.
 *
 * Y **no guarda nada que no haya devuelto el servidor a esta persona**: es la
 * misma información que ya estaba en la memoria del navegador, solo que
 * sobrevive a cerrar la pestaña. Se borra entera al cambiar de negocio y al
 * cerrar sesión, por la misma razón que el catálogo.
 */

import { dehydrate, hydrate, type QueryClient } from "@tanstack/react-query";

import { db } from "./db";

const LLAVE = "consultas_guardadas";

/**
 * Cuánto se espera antes de guardar, después de un cambio.
 *
 * Abrir una pantalla dispara varias consultas seguidas —los datos, los ajustes,
 * las monedas— y guardar en cada una serializaría la caché entera cuatro veces
 * en medio segundo. Con la espera, esas cuatro se juntan en un guardado.
 */
const ESPERA = 2_000;

/**
 * Tope de lo que se guarda, en caracteres del JSON.
 *
 * ⚠️ Hace falta un tope o esto crece para siempre. Un reporte de un año, el
 * listado de tres mil ventas, el histórico de movimientos: cualquiera de esos
 * puede pesar más que todo lo demás junto, y el almacenamiento del navegador de
 * una tablet vieja no es infinito.
 *
 * Cuatro megas dan de sobra para las pantallas de trabajo de un negocio. Lo que
 * no quepa simplemente no se guarda: esa pantalla, sin señal, se verá vacía —
 * como se veía toda la aplicación antes de esto.
 */
const TOPE_TOTAL = 4_000_000;

/** Y ninguna consulta suelta puede pasar de esto. */
const TOPE_POR_CONSULTA = 400_000;

/**
 * Cuánto tiempo tiene sentido enseñar algo guardado.
 *
 * Una semana. Más allá, lo guardado deja de ser «lo que vi esta mañana» y pasa
 * a ser un número de otro mes con pinta de actual — y eso es peor que una
 * pantalla vacía, porque nadie duda de un número que está escrito.
 */
const CADUCA_EN = 7 * 24 * 60 * 60 * 1000;

type Guardado = {
  negocio: string;
  fecha: number;
  estado: unknown;
};

/**
 * Consultas que NO se guardan.
 *
 * ⚠️ La lista es corta a propósito: guardar es lo normal y no guardar es la
 * excepción, porque el objetivo es justo que se vea todo lo que se pueda.
 *
 * Están las que no significan nada al día siguiente. Un turno de caja abierto
 * hace tres días enseñado como si estuviera abierto ahora haría que alguien
 * cerrara caja contra un número que no es. Y la sesión y los permisos se piden
 * siempre de nuevo: quien perdió el acceso no puede seguir entrando porque el
 * aparato se acuerde de que antes podía.
 */
// ⚠️ «caja-actual» es la que de verdad usa la Caja: la lista decía
// «caja-abierta», que no existe, y la caja abierta se guardaba (2026-09-29).
const NO_SE_GUARDAN = ["sesion", "permisos", "yo", "turno-abierto", "caja-abierta", "caja-actual"];

/**
 * Lo CLÍNICO del consultorio tampoco se guarda (2026-10-30): el expediente,
 * las consultas, las fotos, los consentimientos, la bitácora y lo que se busca
 * al recetar o diagnosticar.
 *
 * ⚠️ Es información de salud. La memoria del aparato no está cifrada y la ve
 * quien use esa tableta después; y sin internet el expediente no se consulta
 * (decisión del propietario, `docs/20-CONSULTORIOS.md`). La agenda, la sala de
 * espera y los datos para ubicar al paciente sí se guardan: son de recepción.
 */
const CLINICO_DEL_CONSULTORIO = [
  "expediente",
  "notas",
  "nota",
  "adjuntos",
  "consentimientos",
  "bitacora",
  "versiones",
  "medicamentos",
  "diagnosticos",
];

function seGuarda(clave: readonly unknown[]): boolean {
  const primera = String(clave[0] ?? "");
  if (primera === "consultorio" && CLINICO_DEL_CONSULTORIO.includes(String(clave[1] ?? ""))) return false;
  return !NO_SE_GUARDAN.includes(primera);
}

/**
 * Deja fuera lo que no cabe, empezando por lo más gordo.
 *
 * Se descartan las consultas grandes antes que las pequeñas a propósito: por
 * cada reporte enorme que se tira caben veinte pantallas de trabajo, y son esas
 * veinte las que la persona abre a diario.
 */
function loQueCabe(consultas: { queryKey: readonly unknown[]; state: unknown }[]) {
  const conPeso = consultas
    .map((c) => ({ consulta: c, peso: JSON.stringify(c).length }))
    .filter((c) => c.peso <= TOPE_POR_CONSULTA)
    .sort((a, b) => a.peso - b.peso);

  const cabidas: typeof consultas = [];
  let total = 0;
  for (const { consulta, peso } of conPeso) {
    if (total + peso > TOPE_TOTAL) break;
    total += peso;
    cabidas.push(consulta);
  }
  return cabidas;
}

/**
 * Mete en la caché lo último que se vio, si sigue valiendo.
 *
 * ⚠️ Hay que llamarla ANTES de dibujar nada. Una pantalla que se dibuja primero
 * lanza su petición, falla por falta de señal y enseña el error — y meterle los
 * datos medio segundo después ya no borra ese error de la vista.
 *
 * Devuelve cuándo se guardó, para poder decir «datos de esta mañana» en vez de
 * enseñar cifras viejas sin fecha.
 */
export async function recuperarLasConsultas(
  qc: QueryClient,
  negocio: string | null,
): Promise<number | null> {
  if (!negocio) return null;

  try {
    const fila = await db.meta.get(LLAVE);
    if (!fila?.valor) return null;

    const guardado = JSON.parse(fila.valor) as Guardado;

    // De otro negocio, o demasiado viejo: no se usa y se tira.
    if (guardado.negocio !== negocio || Date.now() - guardado.fecha > CADUCA_EN) {
      await olvidarLasConsultas();
      return null;
    }

    hydrate(qc, guardado.estado);
    return guardado.fecha;
  } catch {
    /*
      Un JSON de una versión anterior, almacenamiento bloqueado, modo incógnito.
      Ninguno de los tres puede impedir que Softfi abra: sin esto, la aplicación
      arranca igual y cada pantalla pide lo suyo, que es como funcionaba antes.
    */
    return null;
  }
}

/**
 * Guarda la foto AHORA MISMO.
 *
 * Va aparte de `recordarLasConsultas` por dos motivos, y ninguno es la prueba:
 * al esconderse la aplicación hay que guardar sin esperar —el temporizador se
 * muere con ella— y al cerrar sesión también.
 */
export async function guardarLasConsultas(
  qc: QueryClient,
  negocio: string | null,
): Promise<void> {
  if (!negocio) return;

  try {
    const estado = dehydrate(qc, {
      /*
        ⚠️ SOLO LO QUE TIENE DATOS.

        Aquí había además un `status === "success"`, y se quitó al descubrir
        —rompiendo el código a propósito para ver si alguna prueba se enteraba—
        que no comprobaba nada: una consulta que falló no tiene datos, así que
        ya se cae por esta misma línea. Dos condiciones donde basta una hacen
        creer que se está protegiendo algo más de lo que se protege.
      */
      shouldDehydrateQuery: (consulta) =>
        consulta.state.data !== undefined && seGuarda(consulta.queryKey),
    }) as { queries: { queryKey: readonly unknown[]; state: unknown }[] };

    const recortado = { ...estado, queries: loQueCabe(estado.queries ?? []) };

    await db.meta.put({
      clave: LLAVE,
      valor: JSON.stringify({
        negocio,
        fecha: Date.now(),
        estado: recortado,
      } satisfies Guardado),
    });
  } catch {
    // Almacenamiento lleno o bloqueado. Que no se pueda guardar la foto no
    // puede romper la pantalla que la persona está usando ahora mismo.
  }
}

/**
 * Empieza a guardar cada vez que llegue algo nuevo.
 *
 * Devuelve la función para dejar de hacerlo.
 */
export function recordarLasConsultas(qc: QueryClient, negocio: string | null): () => void {
  if (!negocio) return () => {};

  let temporizador: ReturnType<typeof setTimeout> | null = null;
  const guardar = () => guardarLasConsultas(qc, negocio);

  const alCambiar = () => {
    if (temporizador) clearTimeout(temporizador);
    temporizador = setTimeout(() => void guardar(), ESPERA);
  };

  const dejarDeMirar = qc.getQueryCache().subscribe(alCambiar);

  /*
    ⚠️ Y AL IRSE DE LA PANTALLA, SIN ESPERAR.

    En un celular, cerrar la aplicación mata el temporizador: lo de los últimos
    dos segundos se perdería justo cuando más importa —el cajero cierra Softfi y
    se va a una zona sin señal—. `visibilitychange` es el único aviso fiable en
    móvil; `beforeunload` ni siquiera llega en iOS.
  */
  const alEsconderse = () => {
    if (document.visibilityState === "hidden") {
      if (temporizador) clearTimeout(temporizador);
      void guardar();
    }
  };
  document.addEventListener("visibilitychange", alEsconderse);

  return () => {
    if (temporizador) clearTimeout(temporizador);
    document.removeEventListener("visibilitychange", alEsconderse);
    dejarDeMirar();
  };
}

/** Tira la foto guardada. Al cambiar de negocio y al cerrar sesión. */
export async function olvidarLasConsultas(): Promise<void> {
  try {
    await db.meta.delete(LLAVE);
  } catch {
    // Ver `recuperarLasConsultas`.
  }
}
